import type { Editor } from "@tiptap/core"
import { Fragment, type Node as PMNode, ResolvedPos, Slice } from "@tiptap/pm/model"
import { TextSelection } from "@tiptap/pm/state"
import { dropPoint } from "@tiptap/pm/transform"
import type { EditorView } from "@tiptap/pm/view"
import { OPERATION_CONTRACTS } from "../analytics/operations"
import { createTitledCard, createUntitledCard, nextCardColor } from "./card"
import {
  dragClipboardNode,
  dragClipboardSlice,
  getDragClipboard,
  withDropIntent,
} from "./clipboard"
import { findCardDepth } from "./find-add-target-pos"
import { isApple } from "./is-apple"

const DEBUG = false
const debugLog = DEBUG ? console.log : () => {}

export const LESSON_DRAG_TYPE = "application/x-cc-lesson"

/**
 * Lesson drags also carry text/html for drops outside the app, so without this check the
 * editor paints a drop cursor for them and pastes that payload on drop.
 */
export function isLessonDrag(event: DragEvent): boolean {
  return event.dataTransfer?.types.includes(LESSON_DRAG_TYPE) ?? false
}

/** The document position under the pointer, or null when the event is outside the doc. */
export function posAtEvent(view: EditorView, event: MouseEvent): number | null {
  return view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos ?? null
}

/**
 * Handles dropping slices, i.e., ProseMirror/HTML content.
 *
 * Files and images are handled separately, by the FileUpload and Image
 * extensions' handleDrop props.
 *
 * @param defaultCard The card type to create when one is needed to contain
 * the dropped content.
 */
export function handleSliceDrop(
  editor: Editor,
  event: DragEvent,
  slice: Slice,
  moved: boolean,
  defaultCard: "titledCard" | "untitledCard",
): boolean {
  // If we're dragging from another editor, use the original editor's info.
  let sourceEditor = moved ? editor : null
  const dragClipboard = getDragClipboard()
  if (dragClipboard) {
    sourceEditor = dragClipboard.sourceEditor
    // The clipboard's move flag is dragstart-time, but a human presses the copy
    // modifier MID-drag (the cursor already shows "+"): move-vs-copy belongs to the
    // DROP event's modifier, exactly as ProseMirror's own handleDrop decides it
    // (prosemirror-view input.ts: `dragging.move && !(mac ? altKey : ctrlKey)`).
    const copyModifierHeld = isApple ? event.altKey : event.ctrlKey
    moved = dragClipboard.move && !copyModifierHeld

    const draggedNode = dragClipboardNode(dragClipboard, editor.schema)
    const clipboardSlice =
      draggedNode !== null
        ? new Slice(Fragment.from(draggedNode), 0, 0)
        : dragClipboardSlice(dragClipboard, editor.schema)
    if (clipboardSlice === null) {
      // This editor's schema can't represent the dragged content (e.g. a card dropped on a
      // note editor). Swallow the drop, leaving both editors as they were.
      if (sourceEditor && sourceEditor !== editor) finishCrossEditorDrag(sourceEditor, false)
      return true
    }
    slice = clipboardSlice

    // The `slice` function arg passes through transformCopied before it gets to us
    // (in prosemirror-view's handleDrop). We need to do analogous processing for
    // the dragClipboardSlice that we retrieved ourselves.
    //
    // Under the drop's intent: a move relocates content that is already ours, so cards keep
    // their identity instead of being re-idded as duplicates (see isMoveDrop).
    withDropIntent(moved, () => {
      editor.view.someProp("transformPasted", (f) => {
        slice = f(slice, editor.view, false)
      })
    })
  }

  // Find out where to insert the slice.
  const sliceType = classifySlice(slice)
  debugLog("HANDLE DROP", moved ? "move" : "copy", sliceType, slice)
  const insertPos = findNearestDropPos(editor, event, sliceType, () => slice)
  if (insertPos === null) {
    // Only possible when posAtCoords can't map the pointer to a doc position. PM's own
    // drop handler starts with the same posAtCoords call and would bail without even
    // preventDefault-ing (exposing the browser's native drop insertion), so don't fall
    // back — swallow the drop, leaving both editors as they were. A no-op "move" keeps
    // the dragged content in the source rather than deleting it.
    debugLog("findNearestDropPos returned null; swallowing the drop")
    if (sourceEditor && sourceEditor !== editor) {
      finishCrossEditorDrag(sourceEditor, false)
    }
    return true
  }

  // Skip the rest of the view's handleDrop function by returning true, but first run that code ourselves,
  // except using our own insertPos.
  // See https://github.com/ProseMirror/prosemirror-view/blob/17af0f4b25933933f644a655ad6bb9a0249d7cb5/src/input.ts#L741
  // (The uiEvent "Drop" meta below lets DragMechanicsSelection treat a node-selecting
  // drop as drag mechanics — no highlight, no bubble menu — while a text-selecting drop
  // stays a normal selection.)

  let inserted = false
  editor
    .chain()
    // A drop that fails to insert sets preventDispatch below, so the labeled transaction never
    // reaches the observer and reports nothing.
    .label(OPERATION_CONTRACTS.OP_DRAG, "drag", {
      moved,
      crossEditor: sourceEditor !== null && sourceEditor !== editor,
    })
    .command(({ tr, commands }) => {
      if (moved && sourceEditor === editor) {
        // Move out of this editor.
        tr.deleteSelection()
      }

      let pos = tr.mapping.map(insertPos)
      const isNode = slice.openStart == 0 && slice.openEnd == 0 && slice.content.childCount == 1

      const $insert = tr.doc.resolve(pos)
      if ($insert.depth === 0 && $insert.index(0) >= 1 && sliceType !== "card") {
        // findNearestDropPos wants us to drop this non-card directly in the lesson (not
        // in an existing card), which requires creating a new card. This happens when
        // there are no cards and you drop on the title, or when the drop lands next to
        // a cardGoogleClassroom (an atom with no block slots of its own).
        const schema = editor.schema
        if (defaultCard === "titledCard") {
          tr.insert(pos, createTitledCard(schema, nextCardColor(tr.doc)))
          // Inside the new card's paragraph: past the card's opening token (1) and the
          // empty cardTitle (2), then into the paragraph (1).
          pos = pos + 4
        } else {
          tr.insert(pos, createUntitledCard(schema))
          // Inside the new card's paragraph: past the card's opening token (1),
          // then into the paragraph (1).
          pos = pos + 2
        }
      }

      const beforeInsert = tr.doc
      if (isNode) tr.replaceRangeWith(pos, pos, slice.content.firstChild!)
      else tr.replaceRange(pos, pos, slice)
      if (tr.doc.eq(beforeInsert)) {
        debugLog("Replacing did nothing")
        // Abort the whole transaction, like PM's own handleDrop (which returns without
        // dispatching). A chain dispatches its tr even from a no-op command, so without
        // this a failed insert would still commit the deleteSelection above — a
        // same-editor move whose insert no-ops would just delete the dragged content.
        tr.setMeta("preventDispatch", true)
        return true
      }
      inserted = true

      const $pos = tr.doc.resolve(pos)
      if (isNode) {
        // Make the selection a cursor near the dropped node.
        // Selecting the whole node pops up the toolbar, which gets in the way.
        tr.setSelection(TextSelection.near($pos))
      } else {
        // Select the dropped content (normal drag-and-drop behavior).
        let end = tr.mapping.map(insertPos)
        tr.mapping.maps[tr.mapping.maps.length - 1].forEach(
          (_from, _to, _newFrom, newTo) => (end = newTo),
        )
        tr.setSelection(TextSelection.between($pos, tr.doc.resolve(end)))
        commands.focus()
      }

      tr.setMeta("uiEvent", "Drop")
      return true
    })
    .focus()
    .run()

  if (sourceEditor && sourceEditor !== editor) {
    finishCrossEditorDrag(sourceEditor, moved && inserted)
  }

  return true
}

/**
 * Cleans up the source editor after its content was dragged to a different editor.
 *
 * When the drop moved (not copied) the content and actually inserted it, delete it from
 * the source via its selection — the same primitive PM uses for same-editor moves. (The
 * drag handle and PM's dragstart handler both select the dragged node, and the selection
 * maps through any transactions that land mid-drag.)
 *
 * Always collapse the source's selection to an empty cursor afterwards: Tiptap's
 * PasteRule plugins schedule deleteRange(source selection) ~10ms after every
 * cross-editor drop — once per paste rule, even for copies and failed inserts
 * (https://github.com/ueberdosis/tiptap/pull/7674). With adjacent atom widgets like
 * standards, each of those deletes would consume the next sibling, because deleting a
 * NodeSelection re-selects the node that slides into its place. Against an empty cursor
 * they are all no-ops. (This stays correct once that PR ships: its single deleteRange
 * also no-ops against the collapsed cursor.)
 */
/**
 * Defuses the source side of a cross-editor drag whose drop is handled outside
 * handleSliceDrop — the note editor's PM-default drop path. Collapses the source's
 * selection (so the post-drop deleteRange timers described above find nothing to
 * delete) and clears its drag state, without deleting anything.
 */
export function defuseCrossEditorDragSource(sourceEditor: Editor): void {
  finishCrossEditorDrag(sourceEditor, false)
}

function finishCrossEditorDrag(sourceEditor: Editor, deleteDragged: boolean) {
  sourceEditor
    .chain()
    .command(({ tr }) => {
      if (deleteDragged) tr.deleteSelection()
      const $pos = tr.selection.$from
      tr.setSelection(TextSelection.between($pos, $pos))
      return true
    })
    .run()

  // Deleting the dragged content detaches the drag-source element, so its dragend never
  // bubbles through the source view and PM's dragend handler
  // (https://github.com/ProseMirror/prosemirror-view/blob/1.41.8/src/input.ts,
  // handlers.dragend — clears view.dragging ~50ms after dragend) never runs. Left
  // stale, a later drop into the source editor from outside any editor (OS text,
  // another app) would be treated as a "move" of this drag's slice. Clearing it also
  // clears the shared drag clipboard, via UnifiedTiptapRenderer's view.dragging monkey-patch.
  sourceEditor.view.dragging = null
}

export type SliceType = "card" | "block" | "singleLineText" | "other"

/**
 * Classify slice based on where it can be dropped.
 */
export function classifySlice(slice: Slice): SliceType {
  if (slice.openStart === 0 && slice.openEnd === 0 && slice.content.childCount === 1) {
    // A single node dragged/copied explicitly, usually via the drag handle.
    const node = slice.content.firstChild!
    if (node.type.isInGroup("card")) return "card"
    if (node.type.isInGroup("block")) return "block"
    return "other"
  }

  const inlineDesc = findOnlyInlineDescendant(slice)
  if (inlineDesc) {
    return "singleLineText"
  }

  return "other"
}

// ----------
// findNearestDropPos
// ----------

/**
 * Return the pos of the nearest place where a slice of the given type can be dropped.
 */
export function findNearestDropPos(
  editor: Editor,
  event: MouseEvent,
  sliceType: SliceType,
  // Only called if needed, to avoid excessive JSON parsing in the drop cursor.
  getSlice: () => Slice,
): number | null {
  const eventPos = posAtEvent(editor.view, event)
  if (eventPos === null) return null
  const $mouse = editor.state.doc.resolve(eventPos)

  switch (sliceType) {
    case "card": {
      const lessonDepth = 0
      const lessonNode = $mouse.node(lessonDepth)

      function skipHeader(indexInLesson: number): number {
        if (
          editor.schema.topNodeType.spec.content?.startsWith("lessonTitle") &&
          indexInLesson === 0
        ) {
          // Skip over the title.
          indexInLesson++
        }
        return indexInLesson
      }

      const preIndexAfter = $mouse.indexAfter(lessonDepth)
      const indexAfter = skipHeader(preIndexAfter)
      const indexBefore = preIndexAfter === 0 ? indexAfter : skipHeader(indexAfter - 1)
      debugLog(
        `Card drop at index ${indexBefore} or ${indexAfter} (from ${preIndexAfter}) in`,
        lessonNode,
      )

      return closest(
        editor,
        event,
        $mouse.posAtIndex(indexBefore, lessonDepth),
        $mouse.posAtIndex(indexAfter, lessonDepth),
      )
    }
    case "block": {
      const cardDepth = findCardDepth($mouse)

      if (cardDepth === null) {
        const doc = editor.state.doc
        if ($mouse.depth > 0 || ($mouse.depth === 0 && $mouse.indexAfter() <= 1)) {
          debugLog("Block drop on lessonTitle")
          // We must be pointing into the lessonTitle or just before/after it.
          // Drop at the beginning of the first card.
          // Except, if there are no cards, clamp to the doc size.
          return Math.min(doc.child(0).nodeSize + 1, doc.content.size)
        } else {
          // We're pointing directly into the lesson, between (or after) cards.
          // Choose from the surrounding cards' first and last block slots. A
          // cardGoogleClassroom (an atom) has no block slots — use the lesson-level gap
          // beside it instead, which handleSliceDrop wraps in a new card.
          const indexAfter = $mouse.indexAfter()
          const beforeCard = doc.child(indexAfter - 1)
          const beforePos =
            beforeCard.type.name === "cardGoogleClassroom" ? $mouse.pos : $mouse.pos - 1
          if (indexAfter === doc.childCount) {
            debugLog("Block drop at end")
            return beforePos
          } else {
            debugLog("Block drop between cards")
            const afterCard = doc.child(indexAfter)
            let afterPos: number
            switch (afterCard.type.name) {
              case "titledCard":
                // After the cardTitle.
                afterPos = $mouse.pos + 1 + afterCard.firstChild!.nodeSize
                break
              case "untitledCard":
                // Just inside the card.
                afterPos = $mouse.pos + 1
                break
              case "cardGoogleClassroom":
                // After the card (which has size 1).
                afterPos = $mouse.pos + 1
                break
              default:
                throw new Error("Not a card type: " + afterCard.type.name)
            }
            return closest(editor, event, beforePos, afterPos)
          }
        }
      } else {
        // Drop the block in the card.
        const card = $mouse.node(cardDepth)
        const firstBlockIndex = card.type.name === "titledCard" ? 1 : 0
        const indexAfter = $mouse.indexAfter(cardDepth)
        if (indexAfter <= firstBlockIndex) {
          debugLog("Block drop at beginning of card")
          // $mouse is <= the first block's top. Drop before all other blocks.
          return $mouse.posAtIndex(firstBlockIndex, cardDepth)
        } else {
          debugLog("Block drop inside card")
          return closest(
            editor,
            event,
            $mouse.posAtIndex(indexAfter - 1, cardDepth),
            $mouse.posAtIndex(indexAfter, cardDepth),
          )
        }
      }
    }
    case "singleLineText":
    case "other": {
      // Like ProseMirror's own drop handling: try dropPoint, then fall back to the mouse
      // position and let replaceRange's fitting sort it out. Returning null here would
      // hand the drop to PM's fallback, which re-parses the slice from dataTransfer HTML
      // and always treats cross-editor drags as copies.
      const slice = getSlice()
      const pos = dropPoint(editor.state.doc, $mouse.pos, slice) ?? $mouse.pos
      // Inserting silently strips marks the target block disallows — e.g. a link
      // dropped into a title (titles allow no marks) would keep its text but lose its
      // href. Land those drops in the nearest block slot instead, where marks survive.
      if (sliceHasMarkDisallowedAt(slice, editor.state.doc.resolve(pos))) {
        return findNearestDropPos(editor, event, "block", getSlice)
      }
      return pos
    }
  }
}

/** Whether inserting `slice` at `$pos` would strip any of its marks. */
function sliceHasMarkDisallowedAt(slice: Slice, $pos: ResolvedPos): boolean {
  let disallowed = false
  slice.content.descendants((node) => {
    if (node.marks.some((mark) => !$pos.parent.type.allowsMarkType(mark.type))) {
      disallowed = true
      return false
    }
  })
  return disallowed
}

/**
 * Returns the pos corresponding to the vertically closer index.
 */
function closest(editor: Editor, event: MouseEvent, posA: number, posB: number): number {
  if (posA === posB) return posA

  const topA = editor.view.coordsAtPos(posA).top
  const topB = editor.view.coordsAtPos(posB).top

  return Math.abs(topA - event.clientY) < Math.abs(topB - event.clientY) ? posA : posB
}

// ----------
// PM helper functions
// ----------

/**
 * Returns the only descendant of the slice with inline content, or null if it does not exist.
 */
function findOnlyInlineDescendant(slice: Slice): PMNode | null {
  let fragment = slice.content
  while (true) {
    if (fragment.childCount === 1) {
      const child = fragment.firstChild!
      if (child.inlineContent) return child
      else fragment = child.content
    } else return null
  }
}
